import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-conversion-rate-benchmark";
const TITLE = "Shopify Conversion Rate: Benchmark & Einordnung";
const DESCRIPTION =
  "Was ist eine gute Conversion Rate bei Shopify? Benchmarks nach Branche, Gerät und Traffic-Quelle, und warum der Durchschnitt oft in die Irre führt.";

const faqs = [
  {
    q: "Was ist eine gute Conversion-Rate bei Shopify?",
    a: "Im DACH-Handel liegt der Durchschnitt grob zwischen 1,2 und 1,8 Prozent. Über 3 Prozent gelten als stark, unter 1 Prozent als klarer Handlungsbedarf. Entscheidend ist aber der Vergleich mit Ihrem eigenen Vormonat, nicht mit einer Branchenzahl.",
  },
  {
    q: "Warum ist meine Conversion-Rate mobil so viel niedriger?",
    a: "Mobile Sessions enthalten mehr Recherche und mehr Zufallsbesuche, dazu kommt der höhere Bedienaufwand. Ein Verhältnis von etwa 1 zu 2 gegenüber Desktop ist üblich, alles darunter deutet auf ein konkretes Problem im mobilen Ablauf hin.",
  },
  {
    q: "Welche Kennzahl ist besser als die Conversion-Rate?",
    a: "Umsatz pro Session. Sie verbindet Kaufabschlussrate und Warenkorb und lässt sich nicht durch billigen Traffic oder Rabattaktionen schönrechnen.",
  },
  {
    q: "Wie stark lässt sich die Conversion-Rate realistisch steigern?",
    a: "Das lässt sich vorab nicht seriös beziffern. Shops, die noch nie strukturiert optimiert wurden, haben in der Regel mehr Spielraum als bereits gründlich optimierte. Belastbar ist am Ende nur die Messung in Ihrem eigenen Shop.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-conversion-rate-benchmark")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Benchmark"
      title="Shopify Conversion Rate: was ist gut, was ist Handlungsbedarf?"
      intro="Die Frage nach dem Benchmark ist fast immer die falsche Frage, und trotzdem der beste Einstieg. Denn wer weiß, wo er steht, kann ausrechnen, was ein Prozentpunkt mehr wert wäre."
      source="ratgeber-benchmark"
      sections={[
        {
          h: "Die groben Orientierungswerte",
          p: [
            "Über alle Shopify-Shops im deutschsprachigen Raum bewegt sich die Kaufabschlussrate meist zwischen 1,2 und 1,8 Prozent. Die Spannweite nach Branche ist allerdings größer als der Abstand zum Durchschnitt.",
          ],
          list: [
            "Verbrauchsgüter und Kosmetik: häufig 2 bis 4 Prozent",
            "Mode und Accessoires: häufig 1 bis 2,5 Prozent",
            "Möbel und hochpreisige Einzelstücke: oft unter 1 Prozent",
            "Ersatzteile und Bedarfskäufe: teils über 5 Prozent",
            "B2B-Shops mit Anmeldung: kaum vergleichbar, Angebotsanfragen zählen mit",
          ],
        },
        {
          h: "Warum der Durchschnitt in die Irre führt",
          p: [
            "Zwei Shops mit identischem Wert können vollkommen unterschiedlich gesund sein. Entscheidend ist die Zusammensetzung des Traffics: Wer viel kalte Reichweite über Social kauft, hat strukturell niedrigere Werte als ein Shop, der überwiegend über Suchanfragen mit Kaufabsicht gefunden wird.",
            "Ein sauberer Vergleich läuft deshalb immer nach Segment: Gerät, Kanal, neu gegen wiederkehrend, Kampagne gegen organisch.",
          ],
        },
        {
          h: "Die vier Zahlen, die zusammen ein Bild ergeben",
          p: [
            "Eine Conversion-Rate allein sagt wenig. Erst im Verbund mit drei weiteren Werten wird daraus eine Diagnose.",
          ],
          list: [
            "Kaufabschlussrate nach Gerät, zeigt mobile Reibung",
            "Warenkorbwert, zeigt, ob Sortiment und Bundles arbeiten",
            "Umsatz pro Session, die ehrlichste Gesamtzahl",
            "Checkout-Abbruchquote pro Schritt, zeigt, wo Geld liegen bleibt",
          ],
        },
        {
          h: "Vom Benchmark zum Geldbetrag",
          p: [
            "Rechne die Lücke aus, statt sie zu bewerten: Sessions × Differenz zur Zielrate × Warenkorb. Aus 25.000 Sessions, 0,7 Prozentpunkten Unterschied und 78 Euro Warenkorb werden rund 13.650 Euro Mehrumsatz im Monat.",
            "Diese Zahl entscheidet, ob Optimierung Priorität hat, und wie viel Budget dafür vertretbar ist.",
          ],
        },
        {
          h: "Der realistische Weg nach oben",
          p: [
            "Sprünge entstehen selten durch eine Idee, sondern durch das Abstellen mehrerer kleiner Reibungen in derselben Kaufstrecke. Produktseite, Warenkorb, Checkout, Ladezeit, in dieser Reihenfolge, weil dort das meiste Geld pro Aufwand liegt.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/roi-rechner", label: "Eigenes Potenzial berechnen" },
        { to: "/ratgeber/shopify-ab-testing", label: "A/B-Testing" },
        { to: "/ratgeber/shopify-conversion-optimierung", label: "Conversion-Leitfaden" },
      ]}
    />
  );
}
